/**
 * Everything the dashboard needs, and the mutations it performs.
 *
 * The full open-item list is fetched once rather than paged. The master list
 * cap is a *display* limit, not a query limit -- the category sections below
 * need every item anyway, so paging the request would mean fetching the same
 * rows twice. For a personal task list this is hundreds of rows at the outside.
 *
 * Mutations are optimistic: the change lands in local state immediately, then
 * the server response reconciles it. On failure the previous state is restored
 * and the server's own message is surfaced, because a status toggle that
 * silently does nothing is worse than one that fails loudly.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { ApiError, api } from '@/api/client'
import type {
  Category,
  DashboardSummary,
  Item,
  ItemCreatePayload,
  ItemQuery,
  ItemUpdatePayload,
  TagWithCount,
} from '@/api/types'
import { CLOSED_STATUSES } from '@/api/types'
import { useToast } from '@/components/Toast'

function isClosed(item: Item): boolean {
  return CLOSED_STATUSES.includes(item.status)
}

export function useDashboard(query: ItemQuery = {}) {
  const toast = useToast()
  const [summary, setSummary] = useState<DashboardSummary | null>(null)
  const [categories, setCategories] = useState<Category[]>([])
  const [tags, setTags] = useState<TagWithCount[]>([])
  const [items, setItems] = useState<Item[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // The query object is rebuilt on every render by useFilters; serialising it
  // gives `load` a dependency that changes only when the filters actually do,
  // instead of on every render (which would refetch in a loop).
  const querySignature = JSON.stringify(query)

  // Guards against a slow response from an earlier load overwriting a newer
  // one, which shows up as the list flickering back to stale data.
  const loadToken = useRef(0)

  const load = useCallback(
    async (options: { quiet?: boolean } = {}) => {
      const token = ++loadToken.current
      if (!options.quiet) setLoading(true)
      try {
        // The item list is fetched first, on purpose. Both it and the
        // dashboard summary can trigger the opportunistic escalation pass, and
        // in parallel the summary could compute its counts from state the pass
        // had not yet committed -- the strip would say "1 imminent" above a
        // list showing four. Sequencing costs one round trip on load and makes
        // the two agree by construction.
        const nextItems = await api.items.list(JSON.parse(querySignature) as ItemQuery)
        const [nextSummary, nextCategories, nextTags] = await Promise.all([
          api.dashboard.summary(),
          api.categories.list(),
          api.tags.list(),
        ])
        if (token !== loadToken.current) return
        setSummary(nextSummary)
        setCategories(nextCategories)
        setTags(nextTags)
        setItems(nextItems.items)
        setTotal(nextItems.total)
        setError(null)
      } catch (caught) {
        if (token !== loadToken.current) return
        // A 401 is handled by the router dropping to the login page; showing
        // an error for it would just be noise on the way out.
        if (caught instanceof ApiError && !caught.isUnauthorized) {
          setError(caught.message)
        }
      } finally {
        if (token === loadToken.current && !options.quiet) setLoading(false)
      }
    },
    [querySignature],
  )

  useEffect(() => {
    void load()
  }, [load])

  /** Apply a patch immediately, then reconcile against the server. */
  const updateItem = useCallback(
    async (id: number, patch: ItemUpdatePayload) => {
      const snapshot = items
      setItems((current) => {
        const patched = current.map((item) =>
          item.id === id ? { ...item, ...patch } : item,
        )
        // A newly closed item leaves the open list at once; waiting for the
        // round trip makes the click feel unacknowledged.
        return patched.filter((item) => item.id !== id || !isClosed(item))
      })

      try {
        await api.items.update(id, patch)
        await load({ quiet: true })
      } catch (caught) {
        setItems(snapshot)
        if (caught instanceof ApiError) toast.error(caught.message)
        else toast.error('Could not save that change.')
      }
    },
    [items, load, toast],
  )

  /** Mark done (or reopen), with an undo in the toast. */
  const completeItem = useCallback(
    async (item: Item) => {
      const previousStatus = item.status
      const nextStatus = isClosed(item) ? 'not_started' : 'done'
      const snapshot = items

      setItems((current) =>
        nextStatus === 'done'
          ? current.filter((candidate) => candidate.id !== item.id)
          : current.map((candidate) =>
              candidate.id === item.id ? { ...candidate, status: nextStatus } : candidate,
            ),
      )

      try {
        await api.items.update(item.id, { status: nextStatus })
        await load({ quiet: true })
        toast.show(nextStatus === 'done' ? `Marked "${item.title}" done` : 'Reopened', {
          tone: 'success',
          action: {
            label: 'Undo',
            onClick: () => {
              void api.items
                .update(item.id, { status: previousStatus })
                .then(() => load({ quiet: true }))
                .catch(() => toast.error('Could not undo that.'))
            },
          },
        })
      } catch (caught) {
        setItems(snapshot)
        if (caught instanceof ApiError) toast.error(caught.message)
        else toast.error('Could not update that item.')
      }
    },
    [items, load, toast],
  )

  const addItem = useCallback(
    async (payload: ItemCreatePayload) => {
      try {
        const created = await api.items.create(payload)
        await load({ quiet: true })
        toast.success(`Added "${created.title}"`)
      } catch (caught) {
        if (caught instanceof ApiError) toast.error(caught.message)
        else toast.error('Could not add that item.')
        throw caught
      }
    },
    [load, toast],
  )

  /** Persist a drag. `orderedIds` is the whole visible list, top first. */
  const reorder = useCallback(
    async (orderedIds: number[]) => {
      const snapshot = items
      const byId = new Map(items.map((item) => [item.id, item]))
      setItems(
        orderedIds
          .map((id) => byId.get(id))
          .filter((item): item is Item => item !== undefined),
      )

      try {
        await api.items.setManualRank(orderedIds)
        await load({ quiet: true })
      } catch (caught) {
        setItems(snapshot)
        if (caught instanceof ApiError) toast.error(caught.message)
        else toast.error('Could not save that order.')
      }
    },
    [items, load, toast],
  )

  /** Return the listed items to automatic ordering. */
  const clearOrder = useCallback(
    async (ids: number[]) => {
      try {
        await api.items.clearManualRank(ids)
        await load({ quiet: true })
        toast.success('Back to automatic order.')
      } catch (caught) {
        if (caught instanceof ApiError) toast.error(caught.message)
        else toast.error('Could not clear the manual order.')
      }
    },
    [load, toast],
  )

  return {
    summary,
    categories,
    tags,
    items,
    total,
    loading,
    error,
    reload: load,
    updateItem,
    completeItem,
    addItem,
    reorder,
    clearOrder,
  }
}
